import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TabViewModule } from 'primeng/tabview';

interface LearningTab {
  key: string;
  title: string;
  description: string;
  videoUrl?: SafeResourceUrl;
}

@Component({
  selector: 'app-learning',
  imports: [CommonModule, TabViewModule],
  templateUrl: './learning.component.html',
  styleUrl: './learning.component.scss'
})
export class LearningComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  activeTabIndex = 0;
  learningTabs: LearningTab[] = [];
  
  isPlaying = false;
  currentTime = 0;
  duration = 0;
  volume = 1;
  isMuted = false;
  private updateInterval: any;

  constructor(private sanitizer: DomSanitizer) {
    // Initialize tabs array with Campaign tab containing the secure video
    this.learningTabs = [
      {
        key: 'campaign',
        title: 'Call Campaign',
        description: 'Learn how to manage call campaigns effectively',
        videoUrl: this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://global-upload-storage.s3.us-east-1.amazonaws.com/education/organizations/Manage+Campaigns+-+Personal+-+Microsoft%E2%80%8B+Edge+2025-11-26+11-05-50.mp4'
        )
      },
      // {
      //   key: 'leads',
      //   title: 'Leads Management',
      //   description: 'Master lead generation and management strategies'
      // },
      // {
      //   key: 'enrollment',
      //   title: 'Enrollment Process',
      //   description: 'Understand the complete enrollment workflow'
      // },
      // {
      //   key: 'reports',
      //   title: 'Reports & Analytics',
      //   description: 'Learn to generate and analyze reports'
      // }
    ];
  }

  ngAfterViewInit(): void {
    const video = this.videoPlayer.nativeElement;
    
    // Disable default controls
    video.controls = false;
    
    // Add security attributes
    video.setAttribute('oncontextmenu', 'return false;');
    video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
    video.setAttribute('disablePictureInPicture', 'true');
    
    // Listen to video events
    video.addEventListener('loadedmetadata', () => {
      this.duration = video.duration;
    });

    video.addEventListener('timeupdate', () => {
      this.currentTime = video.currentTime;
    });

    video.addEventListener('play', () => {
      this.isPlaying = true;
    });

    video.addEventListener('pause', () => {
      this.isPlaying = false;
    });

    // Prevent seeking via keyboard
    video.addEventListener('keydown', (e) => {
      e.preventDefault();
      return false;
    });

    // Start monitoring for screen recording attempts
    this.startSecurityMonitoring();
  }

  ngOnDestroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  // Prevent all unwanted actions
  preventAction(event: Event): boolean {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  // Prevent keyboard shortcuts
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Prevent common screen recording and download shortcuts
    if (
      event.key === 'PrintScreen' ||
      (event.ctrlKey && event.key === 's') || // Save
      (event.ctrlKey && event.key === 'S') ||
      (event.ctrlKey && event.shiftKey && event.key === 'I') || // DevTools
      (event.key === 'F12') || // DevTools
      (event.ctrlKey && event.shiftKey && event.key === 'C') || // DevTools
      (event.ctrlKey && event.shiftKey && event.key === 'J') // DevTools
    ) {
      event.preventDefault();
      return;
    }
  }

  // Prevent right-click context menu
  @HostListener('contextmenu', ['$event'])
  onRightClick(event: Event): void {
    event.preventDefault();
  }

  // Detect when user leaves the tab (possible recording)
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      this.pauseVideo();
    }
  }

  togglePlayPause(): void {
    const video = this.videoPlayer.nativeElement;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  pauseVideo(): void {
    const video = this.videoPlayer.nativeElement;
    if (!video.paused) {
      video.pause();
    }
  }

  seek(event: any): void {
    const video = this.videoPlayer.nativeElement;
    video.currentTime = event.target.value;
  }

  toggleMute(): void {
    const video = this.videoPlayer.nativeElement;
    video.muted = !video.muted;
    this.isMuted = video.muted;
  }

  changeVolume(event: any): void {
    const video = this.videoPlayer.nativeElement;
    video.volume = event.target.value;
    this.volume = event.target.value;
    this.isMuted = this.volume === 0;
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  private startSecurityMonitoring(): void {
    // Check for devtools
    this.updateInterval = setInterval(() => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        // DevTools might be open - pause video
        this.pauseVideo();
      }
    }, 1000);
  }
}
